import React, { FC, ReactNode } from "react";
import useI18n from "@I18n/hooks/useI18n";
import type { Options } from "intl-messageformat";
import {
  FormattedMessage as ReactIntlFormattedMessage,
  MessageDescriptor,
} from "react-intl";

import { Bold } from "@UI/Components/Bold/Bold";
import { Text } from "@UI/Components/Text/Text";
import messages from "./messages";

export type { MessageDescriptor } from "react-intl";

export interface FormattedMessageProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  V extends Record<string, any> = Record<string, ReactNode | (() => ReactNode)>
> extends MessageDescriptor {
  values?: V;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tagName?: React.ElementType<any>;
  children?(nodes: React.ReactNodeArray): React.ReactElement | null;
  ignoreTag?: Options["ignoreTag"];
}

const formatters = {
  bold: (...chunks) => <Bold>{chunks}</Bold>,
  b: (...chunks) => <strong>{chunks}</strong>,
  break: () => <br />,
  inline: (...chunks) => (
    <Text whiteSpace="nowrap" as="span" display="inline-block">
      {chunks}
    </Text>
  ),
  currentYear: new Date(Date.now()).getFullYear(),
};

export const FormattedMessage: FC<FormattedMessageProps> = ({
  values,
  ...props
}) => {
  const { formatMessage } = useI18n();
  const companyName = formatMessage(messages.companyName);
  const companyLegalName = formatMessage(messages.companyLegalName);
  const companyIban = formatMessage(messages.companyIban);
  const companyStartingYear = formatMessage(messages.companyStartingYear);

  const reactIntlProps = {
    ...props,
    values: {
      companyName,
      companyIban,
      companyStartingYear,
      companyLegalName,
      ...formatters,
      ...values,
    },
  };

  return <ReactIntlFormattedMessage {...reactIntlProps} />;
};
